/**
 * BusinessProfile domain types + zod schemas.
 *
 * SOURCE OF TRUTH: `docs/decisions/business/ADR-100-business-profile-registry.md` §2.
 * The server-side Zod schema in `backend/src/domains/business/profile-registry/schema.ts`
 * is the authoritative copy; this frontend schema is a narrowed mirror kept in sync by
 * the CI gate `business-profile-schema-parity` (ADR-100 §2).
 *
 * When the backend schema changes, CI will fail this file until it is updated.
 */
import { z } from 'zod';

export const EngineEnum = z.enum(['E01', 'E02', 'E03', 'E04']);
export type EngineId = z.infer<typeof EngineEnum>;

export const DeliveryModeEnum = z.enum([
  'on_site',
  'mobile',
  'customer_home',
  'remote',
]);
export type DeliveryMode = z.infer<typeof DeliveryModeEnum>;

export const ModuleIdSchema = z
  .string()
  .regex(/^(M\d{2}|M-CONNECT-01|E\d{2}|V-[A-Z]+-\d{2}|C\d{2}|A\d{2})$/);

export const OverbookingPolicyEnum = z.enum([
  'not_allowed',
  'emergency_only',
  'allowed_with_approval',
]);
export type OverbookingPolicy = z.infer<typeof OverbookingPolicyEnum>;

export const SchedulingSchema = z.object({
  slotDurations: z.array(z.number().int().min(5).max(480)).min(1),
  bufferMinutes: z.number().int().min(0).max(120),
  multiResource: z
    .object({
      enabled: z.boolean(),
      resources: z.array(z.string()),
    })
    .optional(),
  overbookingPolicy: OverbookingPolicyEnum,
});
export type SchedulingConfig = z.infer<typeof SchedulingSchema>;

export const ServiceFieldsSchema = z.object({
  required: z.array(z.string()).min(1),
  optional: z.array(z.string()).optional(),
});
export type ServiceFieldsConfig = z.infer<typeof ServiceFieldsSchema>;

export const PricingDefaultsSchema = z.object({
  currency: z.string().length(3),
  pipeline: z.string().optional(),
  stages: z.array(z.record(z.unknown())).optional(),
  formula: z.string().optional(),
});
export type PricingDefaults = z.infer<typeof PricingDefaultsSchema>;

export const ComplianceSchema = z.object({
  requiredCertifications: z.array(z.string()).optional(),
  dataRetentionYears: z.number().int().optional(),
  auditLogRequired: z.boolean().default(true),
});
export type ComplianceConfig = z.infer<typeof ComplianceSchema>;

export const BusinessProfileSchema = z.object({
  subVerticalKey: z.string().regex(/^[a-z][a-z0-9_]+$/),
  displayName: z.string().min(3).max(80),
  profileVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
  engine: EngineEnum,
  deliveryMode: DeliveryModeEnum,
  scheduling: SchedulingSchema,
  modules: z.array(ModuleIdSchema).min(1),
  excludedModules: z.array(z.string()).optional(),
  serviceFields: ServiceFieldsSchema,
  pricingDefaults: PricingDefaultsSchema,
  terminology: z.record(z.string()),
  reportTemplates: z.array(z.string()),
  compliance: ComplianceSchema,
});
export type BusinessProfile = z.infer<typeof BusinessProfileSchema>;

export const PROFILE_STATUSES = [
  'draft',
  'shadow',
  'active',
  'deprecated',
  'archived',
] as const;
export type ProfileStatus = (typeof PROFILE_STATUSES)[number];

export interface BusinessProfileRecord {
  id: string;
  subVerticalKey: string;
  displayName: string;
  profileVersion: string;
  engine: EngineId;
  deliveryMode: DeliveryMode;
  status: ProfileStatus;
  createdBy: string;
  createdAt: string;
  activatedAt?: string;
  deprecatedAt?: string;
  checksumSha256: string;
  profile: BusinessProfile;
}

export const DEPLOYMENT_MODES = ['shadow', 'canary', 'full', 'rollback'] as const;
export type DeploymentMode = (typeof DEPLOYMENT_MODES)[number];

export const TENANT_SCOPES = ['all', 'tenant_list', 'percentage'] as const;
export type TenantScopeKind = (typeof TENANT_SCOPES)[number];

export interface DeploymentRecord {
  id: string;
  profileId: string;
  subVerticalKey: string;
  profileVersion: string;
  deploymentMode: DeploymentMode;
  tenantScope: TenantScopeKind;
  tenantIds?: string[];
  percentage?: number;
  reason: string;
  actorId: string;
  actorName: string;
  correlationId: string;
  createdAt: string;
  rolledBackAt?: string;
  rollbackReason?: string;
}

/**
 * Deploy dialog form contract (reason required — ECTP Rule 7).
 */
export const deployFormSchema = z
  .object({
    mode: z.enum(['shadow', 'canary', 'full']),
    tenantScope: z.enum(['all', 'tenant_list', 'percentage']),
    percentage: z.coerce.number().int().min(1).max(100).optional(),
    tenantIds: z.string().optional(),
    reason: z.string().min(10).max(500),
  })
  .superRefine((val, ctx) => {
    if (val.tenantScope === 'percentage') {
      if (val.percentage === undefined || val.percentage < 1 || val.percentage > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['percentage'],
          message: 'percentageRequired',
        });
      }
    }
    if (val.tenantScope === 'tenant_list') {
      if (!val.tenantIds || val.tenantIds.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tenantIds'],
          message: 'tenantIdsRequired',
        });
      }
    }
  });

export type DeployFormValues = z.infer<typeof deployFormSchema>;
